import { getZodiacFromSolarDate, buildLunarString } from "@/utils/lunarCalc";
import { resolveBirthLocation } from "@/utils/calcTools/astro/geoUtils";
import { resolveUnknownBirthTime } from "@/utils/birthTime";
import type { FourPillars } from "@/utils/baziCalc";
import { buildSharedBaziDetailData } from "@/pages/paipan/sharedPaipanData";
import { NAYIN_MAP } from "@/utils/calcTools/chineseZodiac/wuxingCalc";
import type { Profile } from "@/pages/profileManage/types";
import type { BirthFormData, GeneratedBaziResult } from "./types";
import { fetchAnonymousBaziDetail, fetchAnonymousPaipan } from "@/api/website";
import type { LiuNian, PaipanData } from "@/pages/paipan/types";
import { mapServerProToInsight } from "./serverInsight";
import type { ShenshaItem } from "@/utils/calcTools/bazi/shenshaEngine";

export function normalizeProfileText(value?: string) {
  return (value || "").trim().replace(/\s+/g, " ");
}

export function createNavigateToFunHandler(navigate: (path: string) => void): (e: MessageEvent) => void {
  return (e: MessageEvent) => {
    if (e.data?.type === "NAVIGATE_TO_FUN") {
      navigate("/experience/fun");
    }
  };
}

export function toStoredBirthTime(value: string) {
  return normalizeProfileText(value.replace("T", " "));
}

export function toSolarText(birthTime: string) {
  const stored = toStoredBirthTime(birthTime);
  return stored
    ? `${stored.replace("-", "年").replace("-", "月").replace(" ", "日 ")}時`
    : "";
}

export function calcTrueSolarTime(
  birthTime: string,
  lat: number,
  lng: number,
  tz: number,
): string {
  try {
    const dt = new Date(resolveUnknownBirthTime(birthTime).replace(" ", "T"));
    if (isNaN(dt.getTime())) return "--";

    const dayOfYear = Math.floor(
      (dt.getTime() - new Date(dt.getFullYear(), 0, 0).getTime()) / 86400000,
    );
    const b = (2 * Math.PI * (dayOfYear - 1)) / 365;
    const eot =
      229.18 *
      (0.000075 +
        0.001868 * Math.cos(b) -
        0.032077 * Math.sin(b) -
        0.014615 * Math.cos(2 * b) -
        0.04089 * Math.sin(2 * b));

    const localOffset = (lng - tz * 15) * 4;
    const totalOffsetMin = localOffset + eot;
    const rawTotalMin = Math.round(dt.getHours() * 60 + dt.getMinutes() + totalOffsetMin);
    const h = ((Math.floor(rawTotalMin / 60) % 24) + 24) % 24;
    const m = rawTotalMin - Math.floor(rawTotalMin / 60) * 60;

    const dateStr = birthTime.split(" ")[0] || birthTime.split("T")[0];
    const dateParts = dateStr.split("-");
    let year = parseInt(dateParts[0]);
    let month = parseInt(dateParts[1]);
    let day = parseInt(dateParts[2]);
    if (rawTotalMin < 0) {
      day -= 1;
      if (day < 1) {
        month -= 1;
        if (month < 1) {
          month = 12;
          year -= 1;
        }
        day = new Date(year, month, 0).getDate();
      }
    } else if (rawTotalMin >= 1440) {
      day += 1;
      const daysInMonth = new Date(year, month, 0).getDate();
      if (day > daysInMonth) {
        day = 1;
        month += 1;
        if (month > 12) {
          month = 1;
          year += 1;
        }
      }
    }

    const pad = (n: number) => String(n).padStart(2, "0");
    return `${year}-${pad(month)}-${pad(day)} ${pad(h)}:${pad(m)}`;
  } catch {
    return "--";
  }
}

export function formatCoord(lat: number, lng: number): string {
  const latDir = lat >= 0 ? "北纬" : "南纬";
  const lngDir = lng >= 0 ? "东经" : "西经";
  return `${latDir}${Math.abs(lat).toFixed(2)} ${lngDir}${Math.abs(lng).toFixed(2)}`;
}

export function resolveBirthLocationAtTime(
  birthPlace: string,
  birthTime: string,
): {
  lat: number;
  lng: number;
  tz: number;
} | null {
  if (!birthPlace || !birthTime) return null;
  const [datePart, timePart = "00:00"] = birthTime.split(/[ T]/);
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour] = timePart.split(":").map(Number);
  const resolved = resolveBirthLocation(birthPlace, year, month, day, hour);
  return resolved.isDefault ? null : resolved;
}

export function resolveFormBirthLocation(data: BirthFormData) {
  return resolveBirthLocationAtTime(data.birthPlace, data.birthTime);
}

export function isDuplicateProfile(profile: Profile, data: BirthFormData) {
  return (
    normalizeProfileText(profile.name) === normalizeProfileText(data.name) &&
    profile.gender === data.gender &&
    normalizeProfileText(profile.birthTime) === toStoredBirthTime(data.birthTime) &&
    normalizeProfileText(profile.birthPlace) === normalizeProfileText(data.birthPlace)
  );
}

export function buildProfileFromForm(data: BirthFormData): Profile {
  const birthTime = toStoredBirthTime(data.birthTime);
  let zodiac = "";
  let lunar = "";

  try {
    const [datePart, timePart = "00:00"] = birthTime.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const hour = Number(timePart.split(":")[0] || 0);
    const date = new Date(year, month - 1, day);
    zodiac = getZodiacFromSolarDate(date);
    lunar = buildLunarString(date, hour);
  } catch {
    zodiac = "";
    lunar = "";
  }

  return {
    id: -1,
    name: data.name.trim(),
    gender: data.gender,
    zodiac,
    tag: "other",
    isCurrent: false,
    isExample: false,
    solar: toSolarText(data.birthTime),
    lunar,
    avatar: "",
    birthTime,
    birthPlace: data.birthPlace,
    currentResidence: data.currentResidence,
    calendarType: "solar",
  };
}

export function ganZhiWithNayin(
  item?: { tianGan: string; diZhi: string; naYin: string },
) {
  if (!item) return "";
  return `${item.tianGan}${item.diZhi}${item.naYin ? `（${item.naYin}）` : ""}`;
}

const STEMS10 = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];
const BRANCHES12 = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"];
const XUNCHANG_TABLE: Record<string, { xunShou: string; kongWang: string }> = {
  甲子: { xunShou: "甲子旬", kongWang: "戌亥" },
  甲戌: { xunShou: "甲戌旬", kongWang: "申酉" },
  甲申: { xunShou: "甲申旬", kongWang: "午未" },
  甲午: { xunShou: "甲午旬", kongWang: "辰巳" },
  甲辰: { xunShou: "甲辰旬", kongWang: "寅卯" },
  甲寅: { xunShou: "甲寅旬", kongWang: "子丑" },
};


function stemFromIdx(idx: number): string {
  return STEMS10[((idx % 10) + 10) % 10];
}

function branchFromIdx(idx: number): string {
  return BRANCHES12[((idx % 12) + 12) % 12];
}

function getNaYin(stem: string, branch: string): string {
  return NAYIN_MAP[`${stem}${branch}`] || "";
}

function makeGanZhi(stemIdx: number, branchIdx: number) {
  const tianGan = stemFromIdx(stemIdx);
  const diZhi = branchFromIdx(branchIdx);
  const naYin = getNaYin(tianGan, diZhi);
  return { tianGan, diZhi, naYin };
}

function getXunShouAndKongWang(stem: string, branch: string): { xunShou: string; kongWang: string } {
  const si = STEMS10.indexOf(stem);
  const bi = BRANCHES12.indexOf(branch);
  if (si < 0 || bi < 0) return { xunShou: "", kongWang: "" };
  const xunStartBi = ((bi - si) % 12 + 12) % 12;
  const xunKey = `甲${BRANCHES12[xunStartBi]}`;
  return XUNCHANG_TABLE[xunKey] || { xunShou: `甲${BRANCHES12[xunStartBi]}旬`, kongWang: "" };
}

function calcTaiXiPillar(fp: FourPillars) {
  const stemCombine: Record<string, string> = {
    甲: "己", 己: "甲", 乙: "庚", 庚: "乙", 丙: "辛", 辛: "丙", 丁: "壬", 壬: "丁", 戊: "癸", 癸: "戊",
  };
  const branchCombine: Record<string, string> = {
    子: "丑", 丑: "子", 寅: "亥", 亥: "寅", 卯: "戌", 戌: "卯", 辰: "酉", 酉: "辰", 巳: "申", 申: "巳", 午: "未", 未: "午",
  };
  const tg = stemCombine[fp.day.stem] || fp.day.stem;
  const dz = branchCombine[fp.day.branch] || fp.day.branch;
  return makeGanZhi(STEMS10.indexOf(tg), BRANCHES12.indexOf(dz));
}

function calcShenGongPalace(fp: FourPillars) {
  const monthBranchIdx = BRANCHES12.indexOf(fp.month.branch);
  const hourBranchIdx = BRANCHES12.indexOf(fp.hour.branch);
  const monthSeq = ((monthBranchIdx - 2 + 12) % 12) + 1;
  const hourSeq = hourBranchIdx + 1;
  let shenIdx = (monthSeq + hourSeq + 2) % 12;
  if (shenIdx <= 0) shenIdx += 12;
  const shenGongBranchIdx = (shenIdx - 1) % 12;
  const yearStemIdx = STEMS10.indexOf(fp.year.stem);
  const monthStemStarts = [2, 4, 6, 8, 0, 2, 4, 6, 8, 0];
  const startStemIdx = monthStemStarts[yearStemIdx] ?? 2;
  const shenMonthSeq = ((shenGongBranchIdx - 2 + 12) % 12);
  return makeGanZhi(startStemIdx + shenMonthSeq, shenGongBranchIdx);
}

/**
 * 阴阳参考（对齐问真口径，2026-09-05 校准）。
 * 【已废弃 2026-09-11】四行参考（含阴阳）改为服务端问真口径直读（paipan.info.*），
 * 缺失即抛错。历史本地口径记录：问真以「阳柱数」定档，0~4 → 纯阴/偏阴/平衡/偏阳/阳盛。
 */

function formatTrueSolarFromPillars(birthTime: string, fp: FourPillars): string {
  const correction = fp.solarTimeInfo?.correctionMinutes;
  if (typeof correction !== "number") return birthTime;
  const [datePart, timePart = "12:00"] = birthTime.split(" ");
  const [y, m, d] = datePart.split("-").map(Number);
  const [h, min] = timePart.split(":").map(Number);
  const baseDate = new Date(y, m - 1, d, h, min);
  baseDate.setMinutes(baseDate.getMinutes() + correction);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${baseDate.getFullYear()}-${pad(baseDate.getMonth() + 1)}-${pad(baseDate.getDate())} ${pad(baseDate.getHours())}:${pad(baseDate.getMinutes())}`;
}

function mapServerLiuNian(items: any[]): LiuNian[] {
  return items.map((item) => ({
    year: item.year,
    tianGan: item.tianGan,
    diZhi: item.diZhi,
    tianGanShen: item.tianGanShen,
    diZhiShen: item.diZhiShen,
    tianGanShiShen: item.tianGanShiShen,
    diZhiMainShiShen: item.diZhiMainShiShen,
    liuYue: '',
    liuYue_en: '',
    tianGanColor: item.tianGanColor,
    diZhiColor: item.diZhiColor,
    isHighlight: item.isHighlight,
    shenSha: item.shenSha,
    liuYueList: item.liuYueList,
  }));
}

export function flattenLiuNianGroups(groups: unknown, dayunCount: number): LiuNian[] {
  if (!Array.isArray(groups) || groups.length === 0) return [];

  const seenYears = new Set<number>();
  const result: LiuNian[] = [];
  groups.slice(0, dayunCount).forEach((group) => {
    if (!Array.isArray(group)) return;
    mapServerLiuNian(group).forEach((item) => {
      if (seenYears.has(item.year)) return;
      seenYears.add(item.year);
      result.push(item);
    });
  });
  return result;
}

/**
 * 用服务端匿名 paipan info 覆盖 paipanData 的节气/星座/命卦等字段（去 mock）。
 * 服务端缺失或空值时保留原值（不再回退 mock 写死数据）。
 */
export function applyPaipanInfoToPaipanData(
  paipanData: Record<string, unknown>,
  info: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!info) return paipanData;
  const fields = [
    'lunarDate',
    'prevSolarTermName',
    'prevSolarTermDate',
    'nextSolarTermName',
    'nextSolarTermDate',
    'jieTaiDate',
    'qingMingDate',
    'zodiacSign',
    'chineseConstellation',
    'renYuanSiLing',
    'mingGua',
    'jieQiDesc',
    'shengXiao',
  ] as const;
  for (const key of fields) {
    const value = info[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      (paipanData as Record<string, unknown>)[key] = value;
    }
  }
  // en/ko/ja 的 getter 只读 _en/_ko/_ja 变体，这里用 zh 值兜底，避免多语言界面为空
  const localePairs = [
    ['zodiacSign', ['zodiacSign_en', 'zodiacSign_ko', 'zodiacSign_ja']],
    ['chineseConstellation', ['chineseConstellation_en', 'chineseConstellation_ko', 'chineseConstellation_ja']],
    ['jieQiDesc', ['jieQiDesc_en', 'jieQiDesc_ko', 'jieQiDesc_ja']],
  ] as const;
  for (const [base, variants] of localePairs) {
    const baseValue = info[base];
    if (baseValue !== undefined && baseValue !== null && String(baseValue).trim() !== '') {
      for (const v of variants) {
        (paipanData as Record<string, unknown>)[v] = baseValue;
      }
    }
  }
  return paipanData;
}

/**
 * 服务端 pro.shenSha.items → 前端 ShenshaItem[]（本命神煞，性别由服务端正确计算）。
 */
function mapServerShenshaItems(items: any[]): ShenshaItem[] {
  if (!Array.isArray(items)) return [];
  const VALID_LEVELS = ['大吉', '吉', '中', '注意', '化解'] as const;
  return items.map((item: any) => ({
    name: item.name,
    pillars: Array.isArray(item.pillars) ? item.pillars : [],
    level: (VALID_LEVELS as readonly string[]).includes(item.level) ? item.level as ShenshaItem['level'] : '中',
    desc: item.desc || '',
    resolve: item.resolve || undefined,
    color: item.color || '#8A7B91',
    bg: item.bg || 'rgba(138,123,145,0.1)',
    border: item.border || 'rgba(138,123,145,0.3)',
    influenceScore: item.influenceScore || 0,
  }));
}

export async function buildExperienceBaziResult(
  profile: Profile,
  lang: string,
): Promise<GeneratedBaziResult | null> {
  const birthTime = resolveUnknownBirthTime(profile.birthTime || "");
  if (!birthTime) return null;

  const currentYear = new Date().getFullYear();
  const birthLocation = resolveBirthLocationAtTime(profile.birthPlace || "", birthTime);
  const hasRealLocation = Boolean(birthLocation);

  // 1. 服务端 tyme4j 四柱（仅此来源，失败抛错，不回退本地）
  const server = await fetchAnonymousBaziDetail({
    birthTime,
    gender: profile.gender === 'female' ? 'female' : 'male',
    birthPlace: profile.birthPlace,
    tz: hasRealLocation ? String(birthLocation?.tz ?? 8) : '8',
    lang,
  });
  const serverFourPillars = server?.fourPillars;

  const detailData = buildSharedBaziDetailData({
    birthTime,
    birthPlace: profile.birthPlace,
    birthLongitude: hasRealLocation ? birthLocation?.lng : undefined,
    birthTimezone: hasRealLocation ? birthLocation?.tz : undefined,
    gender: profile.gender,
    currentYear,
    lang,
    serverFourPillars,
  });

  // 1c. 匿名 paipan（Tyme4j）覆盖排盘类字段（大运/流年/起运/命宫）
  const paipan = await fetchAnonymousPaipan({
    birthTime,
    gender: profile.gender === 'female' ? 'female' : 'male',
    birthPlace: profile.birthPlace,
    tz: hasRealLocation ? String(birthLocation?.tz ?? 8) : '8',
    lang,
  });
  const paipanBasic = paipan?.basic;
  if (paipanBasic == null) {
    throw new Error('排盘数据缺失');
  }
  const paipanInfo = paipan?.info;

  const serverPro = mapServerProToInsight(paipan?.pro);

  // ── 四行参考（问真口径）：唯一来源 = paipan info / pro；缺失即抛错，禁止回退前端引擎 ──
  const fourRefs = {
    dayMasterXiXing: paipanInfo?.dayMasterXiXing as string | undefined,
    yangYinRef: paipanInfo?.yangYinRef as string | undefined,
    wangShuaiRef: paipanInfo?.wangShuaiRef as string | undefined,
    geJuRef: paipanInfo?.geJuRef as string | undefined,
  };
  const missingRefs = Object.entries(fourRefs).filter(([, v]) => !v).map(([k]) => k);
  if (missingRefs.length > 0 || !serverPro?.geju?.name || !serverPro?.xiyong) {
    const missing = [...missingRefs];
    if (!serverPro?.geju?.name) missing.push("pro.geJu");
    if (!serverPro?.xiyong) missing.push("pro.yongShen");
    throw new Error(`服务端四行参考缺失: ${missing.join(",")}`);
  }
  // detailData 覆盖 → payload（buildBaziReportData 读 detailData）与专业排盘同源
  // geju：name/level 均以服务端为准（本地 gejuEngine 的档位与服务端口径不一致，实测会分叉）
  detailData.geju = {
    ...detailData.geju,
    name: serverPro.geju.name as typeof detailData.geju.name,
    level: (serverPro.geju.level || detailData.geju.level) as typeof detailData.geju.level,
  };
  // xiyong 整体以服务端为准（喜忌/旺衰/学理口径），页面展示（serverPro）与 payload 同源，避免两套引擎口径分叉
  detailData.xiyong = serverPro.xiyong;

  // 服务端本命神煞覆盖本地（服务端性别正确，含元辰；本地 calcShensha 性别默认男）
  const serverShensha = mapServerShenshaItems(paipan?.pro?.shenSha?.items);
  if (serverShensha.length > 0) {
    detailData.shensha = serverShensha;
    detailData.tableShensha = serverShensha.map(s => ({ ...s }));
  }

  // 1d. Tyme4j daYun/liuNian/起运信息覆盖本地排盘类字段（对齐 App paipan）
  if (Array.isArray(paipanBasic.daYun) && paipanBasic.daYun.length > 0) {
    detailData.professionalDaYun = paipanBasic.daYun.map((dy: any) => ({
      startYear: dy.startYear,
      endYear: dy.endYear,
      ageRange: dy.ageRange || `${dy.startYear}~${dy.endYear}`,
      ageRange_en: dy.ageRange_en,
      tianGan: dy.tianGan,
      diZhi: dy.diZhi,
      tianGanShen: dy.tianGanShen || '',
      diZhiShen: dy.diZhiShen || '',
      tianGanShiShen: dy.tianGanShiShen,
      diZhiMainShiShen: dy.diZhiMainShiShen,
      isActive: dy.isActive,
      tianGanColor: dy.tianGanColor,
      diZhiColor: dy.diZhiColor,
      shenSha: dy.shenSha,
    }));
  }
  const groupedLiuNian = flattenLiuNianGroups(paipanBasic.liuNianGroups, detailData.professionalDaYun.length);
  const currentDayunLiuNian = Array.isArray(paipanBasic.liuNian) ? mapServerLiuNian(paipanBasic.liuNian) : [];
  const baziDetailLiuNian = Array.isArray(server?.liunian?.items) ? mapServerLiuNian(server.liunian.items) : [];
  if (groupedLiuNian.length > 0) {
    detailData.professionalLiuNian = groupedLiuNian;
  } else if (currentDayunLiuNian.length > 0) {
    detailData.professionalLiuNian = currentDayunLiuNian;
  } else if (baziDetailLiuNian.length > 0) {
    detailData.professionalLiuNian = baziDetailLiuNian;
  }
  if (typeof paipanBasic.qiYunInfo === 'string' && paipanBasic.qiYunInfo) {
    detailData.qiYunInfo = paipanBasic.qiYunInfo;
  }
  if (typeof paipanBasic.jiaoYunInfo === 'string' && paipanBasic.jiaoYunInfo) {
    detailData.jiaoYunInfo = paipanBasic.jiaoYunInfo;
  }
  if (typeof paipanBasic.startAge === 'number') {
    detailData.startAge = paipanBasic.startAge;
  }
  if (typeof paipanBasic.startMonths === 'number') {
    detailData.startMonths = paipanBasic.startMonths;
  }
  if (typeof paipanBasic.preciseJiaoYear === 'number') {
    detailData.preciseJiaoYear = paipanBasic.preciseJiaoYear;
  }
  if (typeof paipanBasic.preciseJiaoMonth === 'number') {
    detailData.preciseJiaoMonth = paipanBasic.preciseJiaoMonth;
  }
  if (typeof paipanBasic.isForward === 'boolean') {
    detailData.isForward = paipanBasic.isForward;
  }
  if (paipanBasic.mingGong?.tianGan) {
    detailData.professionalMingGong = {
      tianGan: paipanBasic.mingGong.tianGan,
      diZhi: paipanBasic.mingGong.diZhi,
      naYin: paipanBasic.mingGong.naYin || '',
      tianGanColor: paipanBasic.mingGong.tianGanColor,
      diZhiColor: paipanBasic.mingGong.diZhiColor,
    };
  }
  const { fourPillars } = detailData;
  const professional = detailData.professionalPillars;
  const trueSolarTime = hasRealLocation
    ? fourPillars.solarTimeInfo?.correctionMinutes != null
      ? formatTrueSolarFromPillars(birthTime, fourPillars)
      : calcTrueSolarTime(
          birthTime,
          birthLocation.lat,
          birthLocation.lng,
          birthLocation.tz,
        )
    : birthTime;
  const coordText = hasRealLocation
    ? formatCoord(birthLocation.lat, birthLocation.lng)
    : "";
  const [datePart] = birthTime.split(" ");
  const [birthYear] = datePart.split("-").map(Number);
  const currentAge = Number.isFinite(birthYear) ? currentYear - birthYear : 0;
  const yearXun = getXunShouAndKongWang(fourPillars.year.stem, fourPillars.year.branch);
  const monthXun = getXunShouAndKongWang(fourPillars.month.stem, fourPillars.month.branch);
  const dayXun = getXunShouAndKongWang(fourPillars.day.stem, fourPillars.day.branch);
  const hourXun = getXunShouAndKongWang(fourPillars.hour.stem, fourPillars.hour.branch);
  const taiXi = calcTaiXiPillar(fourPillars);
  const shenGong = calcShenGongPalace(fourPillars);
  const yueling = detailData.yueling;

  const paipanData: Record<string, unknown> = {
    name: profile.name || '',
    gender: profile.gender,
    solarDate: birthTime,
    lunarDate: profile.lunar || '',
    birthPlace: profile.birthPlace || '',
    trueSolarTime,
    coordinates: coordText,
    dayMaster: fourPillars.day.stem,
    shengXiao: profile.zodiac || '',
    nayin: professional[0]?.naYin || '',
    kongWang: dayXun.kongWang,
    kongWang2: dayXun.kongWang.slice(2),
    xunShou: dayXun.xunShou,
    yearKongWang: yearXun.kongWang,
    monthKongWang: monthXun.kongWang,
    dayKongWang: dayXun.kongWang,
    hourKongWang: hourXun.kongWang,
    renYuanSiLing: yueling?.activeDesc || '',
    jieQiDesc: yueling?.activeDesc || '',
    taiYuan: ganZhiWithNayin(
      professional.find((item) => item.label === "胎元"),
    ),
    taiXi: ganZhiWithNayin(taiXi),
    mingGong: ganZhiWithNayin(detailData.professionalMingGong),
    shenGong: ganZhiWithNayin(shenGong),
    dayMasterXiXing: fourRefs.dayMasterXiXing,
    yangYinRef: fourRefs.yangYinRef,
    wangShuaiRef: fourRefs.wangShuaiRef,
    geJuRef: fourRefs.geJuRef,
  };

  // 服务端 info 覆盖节气/星座/命卦等字段（缺失时保留本地值，不回退 mock 写死数据）
  applyPaipanInfoToPaipanData(paipanData, paipanInfo);

  return {
    profile,
    birthTime,
    paipanData: paipanData as unknown as PaipanData,
    detailData,
    currentAge,
    currentYear,
    siLing: detailData.yueling?.activeStem || fourPillars.month.stem,
    serverPro,
  };
}
